import { useState, type ReactNode } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'

const CRUMBS: Record<string, string> = {
  '/accounts': 'Accounts',
  '/research': 'Research a company',
  '/case-studies': 'Case studies',
}

/** Sidebar + topbar + main. Same structure and classes as the prototype's app shell. */
export function AppShell({ children }: { children: ReactNode }) {
  const [navOpen, setNavOpen] = useState(false)
  const [search, setSearch] = useState('')
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { me, signOut } = useAuth()

  // /accounts/:company keeps the Accounts nav item lit and shows the company in the crumb.
  const crumb = pathname.startsWith('/accounts/')
    ? `Accounts · ${decodeURIComponent(pathname.split('/')[2] ?? '')}`
    : (CRUMBS[pathname] ?? 'Accounts')

  const item = ({ isActive }: { isActive: boolean }) => `sb-item${isActive ? ' on' : ''}`

  function submitSearch(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return
    const q = search.trim()
    if (!q) return
    setSearch('')
    // Enter in the global search jumps straight to Research, pre-filled — as before.
    navigate(`/research?company=${encodeURIComponent(q)}`)
  }

  return (
    <div className="app">
      <aside className={`sidebar${navOpen ? ' open' : ''}`}>
        <div className="sb-brand">
          <span className="mark">
            <span style={{ background: '#EF5974' }} />
            <span style={{ background: '#28A0DD' }} />
            <span style={{ background: '#C8D44B' }} />
          </span>
          <span>
            <span className="nm">Burendo</span>
            <span className="sub">Opportunity Intelligence</span>
          </span>
        </div>

        <div className="sb-scroll">
          <NavLink to="/accounts" className={item} onClick={() => setNavOpen(false)}>
            <i className="ti ti-building" /> Accounts
          </NavLink>
          <div className="sb-group">Research</div>
          <NavLink to="/research" className={item} onClick={() => setNavOpen(false)}>
            <i className="ti ti-sparkles" /> Research a company
          </NavLink>
          <NavLink to="/case-studies" className={item} onClick={() => setNavOpen(false)}>
            <i className="ti ti-books" /> Case studies
          </NavLink>
        </div>

        <div className="sb-foot">
          {/* The Settings panel is gone: the API key now lives server-side, and briefs are in
              Postgres, so there is no per-browser state left to configure or back up. */}
          <button className="sb-item" onClick={signOut} title={me?.email ?? ''}>
            <i className="ti ti-logout" /> Sign out
          </button>
        </div>
      </aside>

      <div className="main">
        <div className="topbar">
          <button className="tb-icon mobile-only" aria-label="Menu" onClick={() => setNavOpen((v) => !v)}>
            <i className="ti ti-menu-2" />
          </button>
          <span className="crumb">{crumb}</span>

          <div className="tb-search">
            <i className="ti ti-search" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={submitSearch}
              placeholder="Search accounts, briefs, case studies…"
              autoComplete="off"
            />
          </div>

          <div className="tb-actions">
            <span style={{ fontSize: 13, color: 'var(--slate)', fontWeight: 600 }}>{me?.name}</span>
            <button className="tb-btn pri" onClick={() => navigate('/research')}>
              <i className="ti ti-sparkles" style={{ fontSize: 15 }} /> New brief
            </button>
          </div>
        </div>

        <div className="wrap">{children}</div>
      </div>
    </div>
  )
}
